import { memo, useCallback, useState } from 'react'

import { IContact } from 'clickNgo/entities/contact'
import { ContactPreview } from 'clickNgo/entities/contact/ui'
import { Translation } from 'cng-common/features/translations'
import { Nullable } from 'cng-common/models/utils'
import { IForm } from 'core-components/classes/models/IForm'
import { Button } from 'core-components/library/button'
import { DropdownDynamic } from 'core-components/library/dropdown'
import { ModalDialog } from 'core-components/library/modalDialog'
import { Tooltip } from 'core-components/library/tooltip'
import { useStaticCrud } from 'core-components/types/helpers'

import { useContactData, useReassignment } from '../../contexts'
import { ReassignmentItem } from '../../models'
import { contactDeactivationModalTranslations } from '../../translation'
import { ContactDeactivationFooter } from '../contactDeactivationFooter'
import { ContactDeactivationHeader } from '../contactDeactivationHeader'

import styles from './Reassignment.module.scss'

interface ReassignmentProps {
  title: string
  description: string
  items: ReassignmentItem[]
  onItemChange: (itemKey: string, contact: Nullable<IContact>) => void
  onCancel: () => void
  onProceed: () => void
}

export const Reassignment = memo<ReassignmentProps>(
  ({ title, description, items, onItemChange, onCancel, onProceed }) => {
    const { contact } = useContactData()
    const { availableContacts } = useReassignment()
    const [previewContact, setPreviewContact] = useState<Nullable<IContact>>(null)
    const [isSkipConfirmationVisible, setIsSkipConfirmationVisible] = useState(false)

    const availableContactsSource = useStaticCrud({
      data: availableContacts.map((contactItem: IContact) => ({
        value: contactItem.contactId,
        label: contactItem.email,
        name: `${contactItem.firstName} ${contactItem.lastName}`,
      })),
      keyField: 'value',
    })

    const isEveryItemHasContact = items.every((item) => item.contact !== undefined)

    const handleSkipConfirmationClose = useCallback(() => {
      setIsSkipConfirmationVisible(false)
    }, [])

    const handleSkipConfirmationProceed = useCallback(() => {
      setIsSkipConfirmationVisible(false)
      onProceed()
    }, [onProceed])

    const handlePreviewClose = useCallback(() => {
      setPreviewContact(null)
    }, [])

    const handleItemChange = useCallback(
      (itemKey: string, value: string) => {
        const selectedContact =
          availableContacts.find((c: IContact) => c.contactId === value) || null
        onItemChange(itemKey, selectedContact)
      },
      [availableContacts, onItemChange]
    )

    const handleProceedClick = useCallback(() => {
      if (isEveryItemHasContact) {
        onProceed()
      } else {
        setIsSkipConfirmationVisible(true)
      }
    }, [isEveryItemHasContact, onProceed])

    const renderDropdown = useCallback((item: IForm) => {
      return (
        <span style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '1rem' }}>
          <strong>{item.name}</strong>
          <span>{item.label}</span>
        </span>
      )
    }, [])

    const renderSkipConfirmationContent = useCallback(() => {
      return (
        <>
          <ContactDeactivationHeader
            title={contactDeactivationModalTranslations.skipConfirm.title}
            description={contactDeactivationModalTranslations.skipConfirm.description}
          />
          <div className={styles.confirmButtons}>
            <Button onClick={handleSkipConfirmationClose} color="danger" variant="text">
              <Translation
                text={contactDeactivationModalTranslations.skipConfirm.buttons.cancel}
                span
              />
            </Button>
            <Button onClick={handleSkipConfirmationProceed}>
              <Translation
                text={contactDeactivationModalTranslations.skipConfirm.buttons.confirm}
                span
              />
            </Button>
          </div>
        </>
      )
    }, [handleSkipConfirmationClose, handleSkipConfirmationProceed])

    return (
      <div className={styles.container}>
        <ContactDeactivationHeader
          title={title}
          description={description}
          descriptionParams={[{ key: '{email}', value: contact.email }]}
        />
        <div className={styles.content}>
          {items.map((item) => (
            <div key={item.key} className={styles.item}>
              <div className={styles.itemName}>{item.displayName}</div>
              <div className={styles.contact}>
                <DropdownDynamic
                  classes={{
                    controlClasses: {
                      root: styles.dropdown,
                    },
                  }}
                  label={
                    <Translation
                      text={contactDeactivationModalTranslations.reassignRolesStep.selectContact}
                    />
                  }
                  value={item.contact?.contactId || ''}
                  source={availableContactsSource}
                  onChange={(value) => handleItemChange(item.key, value)}
                  renderFunction={renderDropdown}
                  caption="name"
                />
                <Tooltip
                  tipContent={() => (
                    <Translation
                      text={contactDeactivationModalTranslations.reassignRolesStep.eyeButtonTooltip}
                      span
                    />
                  )}
                >
                  <Button
                    className={styles.eyeButton}
                    iconButton="kbase kbase-eye"
                    color="white"
                    disabled={item.contact === undefined}
                    onClick={() => setPreviewContact(item.contact!)}
                  />
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
        <ContactDeactivationFooter onCancel={onCancel} onProceed={handleProceedClick} />
        <ContactPreview
          isVisible={!!previewContact}
          contact={previewContact!}
          onClose={handlePreviewClose}
        />
        <ModalDialog
          show={isSkipConfirmationVisible}
          onClose={handleSkipConfirmationClose}
          closeOnOutsideClick={false}
          content={renderSkipConfirmationContent}
          size="sm"
        />
      </div>
    )
  }
)

Reassignment.displayName = 'Reassignment'
